import Skeleton from '../../../reusable/Skeleton/Skeleton';
import { t } from '../../../i18n/i18n';
import './Competitions.scss';

/**
 * Placeholders for the Competitions list while `GET /competitions/` is in
 * flight. One endpoint feeds both blocks, but only the grid is per tab —
 * the banner's figures are the same on every one, so `BannerSkeleton` is a
 * first-load state while `CardsSkeleton` shows again on each tab switch.
 *
 * They render the page's own layout classes — `lsb`, `cc-grid`, `cc-card` —
 * so the banner holds its height and the grid its columns at every breakpoint,
 * and the loaded cards drop into the same boxes. The card count is six: a full
 * two rows at the desktop grid, which reflows to two and one with the real one.
 */

const range = (n) => Array.from({ length: n }, (_, i) => i);

/** The Live right now banner. */
export function BannerSkeleton() {
  return (
    <section className="lsb" aria-busy="true" aria-label={t('Loading live competitions')}>
      <header className="lsb__head">
        <span className="lsb__title">
          <Skeleton w="14px" h={14} />
          <Skeleton w="96px" h={13} />
        </span>
        <Skeleton w="72px" h={11} />
      </header>

      <div className="lsb__rule" />

      <div className="lsb__grid">
        {range(2).map((i) => (
          <div className="lsb__cell" key={i}>
            <Skeleton w="min(124px, 80%)" h={11} />
            <Skeleton w="56px" h={24} />
            <Skeleton w="min(104px, 70%)" h={11} />
          </div>
        ))}
      </div>
    </section>
  );
}

/**
 * Just the cards. Used on its own when a tab switch refetches: the section
 * head stays live there, since the trader has already pressed one of those
 * tabs and the controls are what they are waiting on.
 */
export function CardsSkeleton() {
  return (
    <div className="cc-grid" aria-busy="true" aria-label={t('Loading competitions')}>
      {range(6).map((i) => (
        <article className="cc-card" key={i}>
          <div className="cc-card__top">
            <Skeleton w="64px" h={20} pill />
            <Skeleton w="88px" h={11} />
          </div>

          <Skeleton w="min(200px, 80%)" h={18} />
          <Skeleton w="min(240px, 90%)" h={11} />

          <div className="cc-card__row">
            <Skeleton w="64px" h={11} />
            <Skeleton w="92px" h={12} />
          </div>
          <Skeleton w="100%" h={4} pill />

          <div className="cc-card__rule" />

          <div className="cc-card__stats">
            <Skeleton w="88px" h={11} />
            <Skeleton w="96px" h={11} />
          </div>

          {/* The real card ends in a full-width Open competition button. */}
          <Skeleton w="100%" h={40} />
        </article>
      ))}
    </div>
  );
}

/**
 * The whole section, for the first load.
 *
 * The tabs in the section head stay a bar here: a tab the trader can press
 * before there is a list to show would be claiming a state the section can't
 * honour yet.
 */
export function GridSkeleton() {
  return (
    <section className="cc-section" aria-busy="true">
      <header className="cc-section__head">
        <span className="cc-section__title">
          <Skeleton w="14px" h={14} />
          <Skeleton w="112px" h={13} />
        </span>
        <div className="cc-section__controls">
          {/* The four-option tablist. */}
          <Skeleton w="266px" h={32} />
        </div>
      </header>

      <CardsSkeleton />
    </section>
  );
}
